import type { PlatformProductVariantSelectFragment } from '~/api/graphql/fragments/platformProduct.generated';
import type { PlatformProductQueryResponse } from '~/api/graphql/queries/platformProduct.generated';
import type { PlatformProductStatus, VariantPriceStatus } from '~/types/graphql';
import type { Campaign, Variant } from '~/modules/campaign/types';
import type { IPickPageItem } from '~/modules/modal-pick-page';
import type { IGPriceRange } from '~/presentation/common';

import { toProductVariantByBaseID } from '~/modules/campaign';
import { PLATFORM_PRODUCT_PRICE_FIELD } from '../constants';
import { IPickPageType } from '~/modules/modal-pick-page';
import { cleanDecimal } from '~/utils/number';

export type IPlatformProductPriceField =
  (typeof PLATFORM_PRODUCT_PRICE_FIELD)[keyof typeof PLATFORM_PRODUCT_PRICE_FIELD];

export type IVariantPriceData = NonNullable<NonNullable<Campaign['variants']>[number]>['price'];

export type IPlatformProductDetail = PlatformProductQueryResponse['platformProduct'] & {
  /** True when the live read failed — surfaced instead of dropped, since that almost always means the
   * product was deleted in Shopify after being picked. */
  isDeleted?: boolean;
};

export interface IVariantProductInfo {
  title: string;
  handle: string;
  image: string;
  status?: PlatformProductStatus;
}

// PlatformProduct's title/handle/image are nullable over the wire; map to IVariantProductInfo's
// required fields here so callers keep a real, non-optional contract.
export const toVariantProductInfo = (product: IPlatformProductDetail): IVariantProductInfo => ({
  title: product.title ?? '',
  handle: product.handle ?? '',
  image: product.image ?? '',
  status: product.status,
});

export type IVariantPriceRange = IGPriceRange;

export interface IVariantProduct extends IPickPageItem {
  priceRange?: IVariantPriceRange;
}

// Every product has at least one variant, so the card shows one price whenever the variants agree on
// it — a lowest - highest range only says something when they actually differ.
export interface IToVariantPriceRangeParams {
  variants?: PlatformProductVariantSelectFragment[] | null;
  field?: IPlatformProductPriceField;
}

export const toVariantPriceRange = ({
  variants,
  field = PLATFORM_PRODUCT_PRICE_FIELD.PRICE,
}: IToVariantPriceRangeParams): IVariantPriceRange | undefined => {
  const prices = (variants ?? [])
    .map((variant) => (variant[field] ? Number(variant[field]) : NaN))
    .filter((price) => Number.isFinite(price));
  if (!prices.length) return undefined;

  const min = cleanDecimal(Math.min(...prices));
  const max = cleanDecimal(Math.max(...prices));

  return min === max ? { min } : { min, max };
};

/**
 * The Variant's real price range once the campaign runs — not what its duplicate happens to show in
 * Shopify right now, since a saved price only reaches the duplicate when the campaign starts. A
 * Control variant without an override in `productVariants` charges what the Control's own product
 * does, so each one is read from there unless `productVariants` names a price for it specifically.
 */
export interface IToVariantEffectivePriceRangeParams {
  controlVariants?: PlatformProductVariantSelectFragment[] | null;
  productVariants: Variant['productVariants'];
  field?: IPlatformProductPriceField;
}

export const toVariantEffectivePriceRange = ({
  controlVariants,
  productVariants,
  field = PLATFORM_PRODUCT_PRICE_FIELD.PRICE,
}: IToVariantEffectivePriceRangeParams): IVariantPriceRange | undefined => {
  const overrideByBaseID = toProductVariantByBaseID(productVariants);

  const effectiveVariants = (controlVariants ?? []).map((variant) => {
    const override = overrideByBaseID.get(variant.baseID);
    return override?.[field] != null ? { ...variant, [field]: override[field] } : variant;
  });

  return toVariantPriceRange({ variants: effectiveVariants, field });
};

// title/handle/image live on the Shopify product and can change after the price was picked, so they're
// always overridden with freshly fetched platformProduct data when available instead of the stored price.
export const toPickPageItemFromVariantPrice = (
  price: NonNullable<IVariantPriceData>,
  productInfo?: IVariantProductInfo,
): IPickPageItem => ({
  id: price.id,
  baseID: price.baseID,
  handle: productInfo?.handle ?? price.handle,
  title: productInfo?.title ?? price.title ?? '',
  url: price.redirectURL ?? '',
  type: IPickPageType.PRODUCT,
  status: toVariantPriceStatus(productInfo?.status) ?? price.status,
  image: productInfo?.image,
});

// redirectURL is authoritative when set (Markets locale, custom domain); handle+shopURL is the fallback
// for a price that hasn't been saved to the server yet (e.g. still being edited, unsaved changes).
export const getVariantPriceLiveURL = (
  price?: IVariantPriceData,
  shopURL?: string,
): string | undefined => {
  if (price?.redirectURL) return price.redirectURL;
  if (!price?.handle || !shopURL) return undefined;

  return `${shopURL}products/${price.handle}`;
};

export interface IIsVariantPriceDuplicatedParams {
  controlPrice?: IVariantPriceData;
  variantPrice?: IVariantPriceData;
}

/**
 * Whether the Variant product is still a valid duplicate of the Control's current product.
 * `sourceProductBaseID` is the duplicate's own record of which product it was copied from — set by
 * the service that creates it — so a Control re-pointed at another product makes it disagree with
 * the Control's live baseID, which is exactly when a fresh duplicate is due.
 */
export const isVariantPriceDuplicated = ({
  controlPrice,
  variantPrice,
}: IIsVariantPriceDuplicatedParams): boolean =>
  !!controlPrice?.baseID &&
  !!variantPrice?.baseID &&
  variantPrice.sourceProductBaseID === controlPrice.baseID;

export interface IToCampaignWithVariantPriceParams {
  campaign: Campaign;
  variantID: string;
  price: Variant['price'];
}

export const toCampaignWithVariantPrice = ({
  campaign,
  variantID,
  price,
}: IToCampaignWithVariantPriceParams): Campaign => ({
  ...campaign,
  variants: campaign.variants?.map((variant) =>
    variant?.id === variantID ? { ...variant, price } : variant,
  ),
});

export const toVariantPriceStatus = (
  status?: PlatformProductStatus,
): VariantPriceStatus | undefined => {
  if (!status || status === 'ARCHIVED') return undefined;

  return status;
};

// baseID is a platform GID (gid://shopify/Product/123); Shopify's product search syntax filters by the numeric id.
const toNumericPlatformProductId = (baseID: string): string => baseID.split('/').pop() ?? baseID;

export const toPlatformProductIdsQuery = (baseIDs: string[]): string =>
  `(${baseIDs.map((baseID) => `id:${toNumericPlatformProductId(baseID)}`).join(' OR ')})`;
